import React, { useEffect, useState } from 'react';
import { apiBaseUrl } from '../../config';
import axios from 'axios';

const Items = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [selectedItem, setSelectedItem] = useState(null);
  const [types, setTypes] = useState([]);
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  
  // All item types
  const itemTypes = [
    // Original Types
    'philosophy',
    'service', 
    'product',
    'step',
    'trajectory',
    'data',
    'feature',
    
    // Platform Page Types
    'platform_input',
    'platform_layer',
    'algorithm_step',
    'ai_fit',
    
    // Science Page Types
    'science_input',
    'science_evidence',
    'science_card',
    'science_comparison',
    
    // Services Page Types
    'services_evidence',
    'services_solution',
    
    // Quick Page Types
    'quick_input',
    'quick_domain',
    'quick_who',
    'quick_comparison',
    
    // About Page Types
    'about_principle',
    'about_timeline',
    
    // Contact Page Types
    'contact_service',
    'contact_directory',
    'layer',
    // Accordion
    'accordion'
  ];
  // Status options for products
  const statusOptions = [
    'Launch Product',
    'In Development / Early Access Deployments',
    'Pilot Partnerships Active',
    'Partnership Development'
  ];

  // Badge options
  const badgeOptions = [
    'badge-success',
    'badge-warning', 
    'badge-primary',
    'badge-info',
    'badge-danger',
    'badge-secondary'
  ];

  // Icon options (Font Awesome)
  const iconOptions = [
    'fa-file-medical',
    'fa-chart-line',
  
    'fa-heartbeat',
    'fa-search',
    'fa-user-md',
    'fa-line-chart',
    'fa-stethoscope',
    'fa-hospital',
    'fa-building',
    'fa-globe',
    'fa-dna',
    'fa-running',
    'fa-file-medical',
    'fa-chart-line',
    'fa-brain'
  ];

  const [formData, setFormData] = useState({
    title: '',
    type: '',
    status: '',
    badge: '',
    description: '',
    icon: ''
  });

  useEffect(() => {
    fetchItems();
    fetchTypes();
  }, [currentPage, filterType, filterStatus]);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const params = { page: currentPage, limit: 10 };
      if (filterType) params.type = filterType;
      if (filterStatus) params.status = filterStatus;

      const response = await axios.get(apiBaseUrl + '/items', { params });
      setItems(response.data.data);
      setTotalPages(response.data.pagination?.totalPages || 0);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error('Error fetching items:', error);
      alert('Failed to fetch items');
    }
  };

  const fetchTypes = async () => {
    try {
      const response = await axios.get(apiBaseUrl + '/types');
      setTypes(response.data.data || []);
    } catch (error) {
      console.error('Error fetching types:', error);
    }
  };

  const createItem = async (event) => {
    event.preventDefault();
    const form = event.target;
    const formDataObj = new FormData(form);
    const data = Object.fromEntries(formDataObj.entries());

    try {
      const response = await axios.post(apiBaseUrl + '/item/create', data);
      if (response.data.status) {
        alert('Item created successfully!');
        closeModal();
        fetchItems();
        resetForm();
      } else {
        alert(response.data.message || 'Failed to create item');
      }
    } catch (error) {
      console.error('Error creating item:', error);
      alert(error.response?.data?.message || 'Error creating item');
    }
  };

  const updateItem = async (event) => {
    event.preventDefault();
    const form = event.target;
    const formDataObj = new FormData(form);
    const data = Object.fromEntries(formDataObj.entries());

    try {
      const response = await axios.put(
        apiBaseUrl + `/item/${selectedItem.id}`,
        data
      );
      if (response.data.status) {
        alert('Item updated successfully!');
        closeEditModal();
        fetchItems();
      } else {
        alert(response.data.message || 'Failed to update item');
      }
    } catch (error) {
      console.error('Error updating item:', error);
      alert(error.response?.data?.message || 'Error updating item');
    }
  };

  const deleteItem = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this item?')) {
      return;
    }

    try {
      const response = await axios.delete(apiBaseUrl + `/item/${itemId}`);
      if (response.data.status) {
        alert('Item deleted successfully!');
        fetchItems();
      } else {
        alert(response.data.message || 'Failed to delete item');
      }
    } catch (error) {
      console.error('Error deleting item:', error);
      alert(error.response?.data?.message || 'Error deleting item');
    }
  };

  const editRecord = (item) => {
    setSelectedItem(item);
    setFormData({
      title: item.title || '',
      type: item.type || '',
      status: item.status || '',
      badge: item.badge || '',
      description: item.description || '',
      icon: item.icon || ''
    });
    openEditModal();
  };

  const resetForm = () => {
    setFormData({
      title: '',
      type: '',
      status: '',
      badge: '',
      description: '',
      icon: ''
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const openModal = () => {
    setIsOpen(true);
    document.body.classList.add('modal-open');
  };
  
  const closeModal = () => {
    setIsOpen(false);
    document.body.classList.remove('modal-open');
    resetForm();
  };

  const openEditModal = () => {
    setIsEditOpen(true);
    document.body.classList.add('modal-open');
  };
  
  const closeEditModal = () => {
    setIsEditOpen(false);
    document.body.classList.remove('modal-open');
    setSelectedItem(null);
    resetForm();
  };

  const handlePageChange = (page) => setCurrentPage(page);

  const getTypeLabel = (type) => {
    const labels = {
      philosophy: 'Philosophy',
      service: 'Service',
      product: 'Product',
      step: 'Step',
      trajectory: 'Trajectory',
      data: 'Data',
      feature: 'Feature'
    };
    return labels[type] || type;
  };

  const renderPagination = () => {
    const pageNumbers = [];
    for (let i = 1; i <= totalPages; i++) {
      pageNumbers.push(i);
    }

    return (
      <ul className="pagination" style={{ float: 'right' }}>
        {currentPage > 1 && (
          <li className="page-item" onClick={() => handlePageChange(1)}>
            <button className="page-link">&laquo; First</button>
          </li>
        )}
        {currentPage > 1 && (
          <li className="page-item" onClick={() => handlePageChange(currentPage - 1)}>
            <button className="page-link">&lt; Previous</button>
          </li>
        )}
        {pageNumbers.map((number) => (
          <li
            key={number}
            className={`page-item ${currentPage === number ? 'active' : ''}`}
            onClick={() => handlePageChange(number)}
          >
            <button className="page-link">{number}</button>
          </li>
        ))}
        {currentPage < totalPages && (
          <li className="page-item" onClick={() => handlePageChange(currentPage + 1)}>
            <button className="page-link">Next &gt;</button>
          </li>
        )}
        {currentPage < totalPages && (
          <li className="page-item" onClick={() => handlePageChange(totalPages)}>
            <button className="page-link">Last &raquo;</button>
          </li>
        )}
      </ul>
    );
  };

  return (
    <div className="content-wrapper">
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h1 className="m-0">Content Management</h1>
            </div>
            <div className="col-sm-6">
              <ol className="breadcrumb float-sm-right">
                <li className="breadcrumb-item"><a href="#">Home</a></li>
                <li className="breadcrumb-item active">Content</li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      <section className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <div className="row">
                    <div className="col-md-3">
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={openModal}
                      >
                        <i className="fas fa-plus"></i> Add Content
                      </button>
                    </div>
                    <div className="col-md-9">
                      <div className="float-right">
                        <select
                          className="form-control form-control-sm"
                          style={{ display: 'inline-block', width: 'auto', marginRight: '5px' }}
                          value={filterType}
                          onChange={(e) => setFilterType(e.target.value)}
                        >
                          <option value="">All Types</option>
                          {itemTypes.map((type, index) => (
                            <option key={index} value={type}>{getTypeLabel(type)}</option>
                          ))}
                        </select>
                        <select
                          className="form-control form-control-sm"
                          style={{ display: 'inline-block', width: 'auto' }}
                          value={filterStatus}
                          onChange={(e) => setFilterStatus(e.target.value)}
                        >
                          <option value="">All Statuses</option>
                          {statusOptions.map((status, index) => (
                            <option key={index} value={status}>{status}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="card-body">
                  {loading ? (
                    <div className="text-center">
                      <i className="fas fa-spinner fa-spin fa-2x"></i>
                    </div>
                  ) : (
                    <>
                      <table className="table table-bordered table-striped">
                        <thead>
                          <tr>
                            <th>S.No</th>
                            <th>Type</th>
                            <th>Title</th>
                            <th>Status</th>
                            <th>Icon</th>
                            <th>Description</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {items.length > 0 ? (
                            items.map((item, index) => (
                              <tr key={item.id}>
                                <td style={{ textAlign: 'center' }}>
                                  {(currentPage - 1) * 10 + index + 1}
                                </td>
                                <td>
                                  <span className="badge badge-primary">
                                    {getTypeLabel(item.type)}
                                  </span>
                                </td>
                                <td><strong>{item.title}</strong></td>
                                <td>
                                  {item.badge ? (
                                    <span className={`badge ${item.badge}`}>
                                      {item.status || 'Active'}
                                    </span>
                                  ) : (
                                    item.status || 'N/A'
                                  )}
                                </td>
                                <td>
                                  {item.icon && <i className={`fas ${item.icon}`}></i>}
                                </td>
                                <td>{item.description || 'N/A'}</td>
                                <td style={{ textAlign: 'center' }}>
                                  <button
                                    className="btn btn-info btn-sm mr-1"
                                    onClick={() => editRecord(item)}
                                  >
                                    <i className="fas fa-edit"></i>
                                  </button>
                                  <button
                                    className="btn btn-danger btn-sm"
                                    onClick={() => deleteItem(item.id)}
                                  >
                                    <i className="fas fa-trash"></i>
                                  </button>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="7" className="text-center">
                                No items found
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                      {totalPages > 1 && renderPagination()}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Create Modal */}
      <div 
        className={`modal ${isOpen ? 'show d-block' : ''}`} 
        tabIndex="-1" 
        role="dialog"
        style={{ 
          backgroundColor: isOpen ? 'rgba(0,0,0,0.5)' : 'transparent',
          display: isOpen ? 'block' : 'none',
          overflowY: 'auto',
          zIndex: 1050
        }}
      >
        <div className="modal-dialog modal-lg" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Create Content</h5>
              <button
                type="button"
                className="close"
                onClick={closeModal}
              >
                <span aria-hidden="true">×</span>
              </button>
            </div>
            <div className="modal-body">
              <form onSubmit={createItem}>
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Type *</label>
                      <select
                        className="form-control"
                        name="type"
                        value={formData.type}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select Type</option>
                        {itemTypes.map((type, index) => (
                          <option key={index} value={type}>{getTypeLabel(type)}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Title *</label>
                      <input
                        type="text"
                        className="form-control"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Status</label>
                      <select
                        className="form-control"
                        name="status"
                        value={formData.status}
                        onChange={handleInputChange}
                      >
                        <option value="">Select Status</option>
                        {statusOptions.map((status, index) => (
                          <option key={index} value={status}>{status}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Badge</label>
                      <select
                        className="form-control"
                        name="badge"
                        value={formData.badge}
                        onChange={handleInputChange}
                      >
                        <option value="">Select Badge</option>
                        {badgeOptions.map((badge, index) => (
                          <option key={index} value={badge}>
                            {badge.replace('badge-', '').toUpperCase()}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Icon</label>
                      <select
                        className="form-control"
                        name="icon"
                        value={formData.icon}
                        onChange={handleInputChange}
                      >
                        <option value="">Select Icon</option>
                        {iconOptions.map((icon, index) => (
                          <option key={index} value={icon}>
                            <i className={`fas ${icon}`}></i> {icon}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="col-md-12">
                    <div className="form-group">
                      <label>Description</label>
                      <textarea
                        className="form-control"
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        rows="4"
                      />
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={closeModal}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Create Item
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      <div 
        className={`modal ${isEditOpen ? 'show d-block' : ''}`} 
        tabIndex="-1" 
        role="dialog"
        style={{ 
          backgroundColor: isEditOpen ? 'rgba(0,0,0,0.5)' : 'transparent',
          display: isEditOpen ? 'block' : 'none',
          overflowY: 'auto',
          zIndex: 1050
        }}
      >
        <div className="modal-dialog modal-lg" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Update Content</h5>
              <button
                type="button"
                className="close"
                onClick={closeEditModal}
              >
                <span aria-hidden="true">×</span>
              </button>
            </div>
            <div className="modal-body">
              <form onSubmit={updateItem}>
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Type *</label>
                      <select
                        className="form-control"
                        name="type"
                        value={formData.type}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select Type</option>
                        {itemTypes.map((type, index) => (
                          <option key={index} value={type}>{getTypeLabel(type)}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Title *</label>
                      <input
                        type="text"
                        className="form-control"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Status</label>
                      <select
                        className="form-control"
                        name="status"
                        value={formData.status}
                        onChange={handleInputChange}
                      >
                        <option value="">Select Status</option>
                        {statusOptions.map((status, index) => (
                          <option key={index} value={status}>{status}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Badge</label>
                      <select
                        className="form-control"
                        name="badge"
                        value={formData.badge}
                        onChange={handleInputChange}
                      >
                        <option value="">Select Badge</option>
                        {badgeOptions.map((badge, index) => (
                          <option key={index} value={badge}>
                            {badge.replace('badge-', '').toUpperCase()}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Icon</label>
                      <select
                        className="form-control"
                        name="icon"
                        value={formData.icon}
                        onChange={handleInputChange}
                      >
                        <option value="">Select Icon</option>
                        {iconOptions.map((icon, index) => (
                          <option key={index} value={icon}>
                            <i className={`fas ${icon}`}></i> {icon}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="col-md-12">
                    <div className="form-group">
                      <label>Description</label>
                      <textarea
                        className="form-control"
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        rows="4"
                      />
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={closeEditModal}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Update Item
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {isOpen && <div className="modal-backdrop fade show"></div>}
      {isEditOpen && <div className="modal-backdrop fade show"></div>}
    </div>
  );
};

export default Items;